import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import socketService from '../services/socketService';

export const useSocket = (userId) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [connectionDetails, setConnectionDetails] = useState({});
  const listenersRef = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const staleConnectionCheckRef = useRef(null);

  // Connect to socket when userId is provided
  useEffect(() => {
    if (userId) {
      socketService.connect(userId);
      
      // setup stale connection check (every 15 minutes)
      staleConnectionCheckRef.current = setInterval(() => {
        if (socketService.isConnectionStale()) {
          socketService.refreshStaleConnection();
        }
      }, 15 * 60 * 1000); // 15 minutes
      
    } else {
      socketService.disconnect();
      setIsConnected(false);
      setConnectionStatus('disconnected');
      setConnectionDetails({});
    }

    return () => {
      if (userId) {
        socketService.disconnect();
      }
      
      // clear stale connection check
      if (staleConnectionCheckRef.current) {
        clearInterval(staleConnectionCheckRef.current);
        staleConnectionCheckRef.current = null;
      }
    };
  }, [userId]); // Remove isConnected dependency to prevent infinite loops



  // Listen for connection status changes
  useEffect(() => {
    const unsubscribe = socketService.addEventListener('connection', (data) => {
      setIsConnected(data.connected);
      
      if (data.connected) {
        setConnectionStatus('connected');
        
        // clear any reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        // update connection details
        setConnectionDetails(socketService.getDetailedConnectionStatus());
        
      } else if (data.reconnecting) {
        setConnectionStatus('reconnecting');
        
      } else if (data.reconnectionFailed) {
        setConnectionStatus('failed');
        
        // schedule manual reconnection attempt
        if (userId && !reconnectTimeoutRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (userId && !socketService.isConnected) {
              socketService.retryConnection();
            }
            reconnectTimeoutRef.current = null;
          }, 5000);
        }
        
      } else {
        setConnectionStatus('disconnected');
        
        // schedule reconnection if we have userId
        if (userId && !reconnectTimeoutRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (userId && !socketService.isConnected) {
              socketService.retryConnection();
            }
            reconnectTimeoutRef.current = null;
          }, 2000);
        }
      }
    });

    return unsubscribe;
  }, [isConnected, userId]);

  // Check if a specific user is online
  const isUserOnline = useCallback((userId) => {
    // For now, return true for demo purposes
    // In a real app, you'd track online users from the backend
    return true;
  }, []);

  // Add event listener
  const addEventListener = useCallback((event, callback) => {
    const unsubscribe = socketService.addEventListener(event, callback);
    
    // Store the unsubscribe function
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event).add(unsubscribe);
    
    return unsubscribe;
  }, []);

  // Remove event listener
  const removeEventListener = useCallback((event, callback) => {
    const eventListeners = listenersRef.current.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        listenersRef.current.delete(event);
      }
    }
  }, []);

  // Send message
  const sendMessage = useCallback((messageData) => {
    return socketService.sendMessage(messageData);
  }, []);

  // Send message on sub-conversation
  const sendMessageOnSubConversation = useCallback((messageData) => {
    return socketService.sendMessageOnSubConversation(messageData);
  }, []);

  // Send message on mini-conversation
  const sendMessageOnMiniConversation = useCallback((messageData) => {
    return socketService.sendMessageOnMiniConversation(messageData);
  }, []);

  // Join conversation
  const joinConversation = useCallback((conversationId) => {
    return socketService.joinConversation(conversationId);
  }, []);

  // Leave conversation
  const leaveConversation = useCallback((conversationId) => {
    return socketService.leaveConversation(conversationId);
  }, []);

  // Emit typing status
  const emitTyping = useCallback((conversationId, isTyping) => {
    return socketService.emitTyping(conversationId, isTyping);
  }, []);

  // Get conversations
  const getConversations = useCallback(() => {
    return socketService.getConversations();
  }, []);

  // Get messages
  const getMessages = useCallback((conversationId, lastMessageId) => {
    return socketService.getMessages(conversationId, lastMessageId);
  }, []);

  // Get messages for mini conversation
  const getMessagesMiniConversation = useCallback((conversationId, lastMessageId) => {
    return socketService.getMessagesMiniConversation(conversationId, lastMessageId);
  }, []);

  // Mark conversation as read
  const markAsRead = useCallback((conversationId) => {
    return socketService.markAsRead(conversationId);
  }, []);

  // Get conversation last updated data
  const getConversationLastUpdated = useCallback((conversationId) => {
    return socketService.getConversationLastUpdated(conversationId);
  }, []);

  // Get conversation details
  const getConversationDetails = useCallback((conversationId) => {
    return socketService.getConversationDetails(conversationId);
  }, []);

  // Generic emit method for custom events
  const emit = useCallback((eventName, data) => {
    return socketService.emit(eventName, data);
  }, []);

  // Manual connection retry
  const retryConnection = useCallback(() => {
    return socketService.retryConnection();
  }, []);

  // Force reconnection (for page refresh scenarios)
  const forceReconnect = useCallback(() => {
    return socketService.forceReconnect();
  }, []);

  // Get detailed connection status
  const getDetailedConnectionStatus = useCallback(() => {
    return socketService.getDetailedConnectionStatus();
  }, []);

  // Test server connectivity
  const testServerConnectivity = useCallback(async () => {
    return socketService.testServerConnectivity();
  }, []);

  // Check connection health
  const checkConnectionHealth = useCallback(() => {
    const status = socketService.getDetailedConnectionStatus();
    setConnectionDetails(status);
    return status;
  }, []);

  // Refresh stale connection
  const refreshStaleConnection = useCallback(() => {
    return socketService.refreshStaleConnection();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    const currentListeners = listenersRef.current;
    const currentReconnectTimeout = reconnectTimeoutRef.current;
    const currentStaleConnectionCheck = staleConnectionCheckRef.current;

    return () => {
      // Clean up all listeners
      currentListeners.forEach((unsubscribes) => {
        unsubscribes.forEach(unsubscribe => unsubscribe());
      });
      currentListeners.clear();
      
      // clear reconnect timeout
      if (currentReconnectTimeout) {
        clearTimeout(currentReconnectTimeout);
      }
      
      // clear stale connection check
      if (currentStaleConnectionCheck) {
        clearInterval(currentStaleConnectionCheck);
      }
    };
  }, []);

  return useMemo(() => ({
    isConnected,
    connectionStatus,
    connectionDetails,
    socketService,
    isUserOnline,
    addEventListener,
    removeEventListener,
    sendMessage,
    sendMessageOnSubConversation,
    sendMessageOnMiniConversation,
    joinConversation,
    leaveConversation,
    emitTyping,
    getConversations,
    getMessages,
    getMessagesMiniConversation,
    markAsRead,
    getConversationLastUpdated,
    getConversationDetails,
    emit,
    retryConnection,
    forceReconnect,
    getDetailedConnectionStatus,
    testServerConnectivity,
    checkConnectionHealth,
    refreshStaleConnection
  }), [
    isConnected,
    connectionStatus,
    connectionDetails,
    isUserOnline,
    addEventListener,
    removeEventListener,
    sendMessage,
    sendMessageOnSubConversation,
    sendMessageOnMiniConversation,
    joinConversation,
    leaveConversation,
    emitTyping,
    getConversations,
    getMessages,
    getMessagesMiniConversation,
    markAsRead,
    getConversationLastUpdated,
    getConversationDetails,
    emit,
    retryConnection,
    forceReconnect,
    getDetailedConnectionStatus,
    testServerConnectivity,
    checkConnectionHealth,
    refreshStaleConnection
  ]);
};
